import React from 'react';
import { useForm } from 'react-hook-form';
import { Save, Cancel } from '@mui/icons-material';
import { Box, Stack, Divider, Button } from '@mui/material';
import { convertEnumToSelectOptions } from 'utils/selectOptionsHelper';
import { InputField, SelectField, DateField, AutocompleteField } from 'components/form';
import { DealActivityStatus, DealActivityEventType, DealActivityLinkType } from '@trnsact/trnsact-shared-types';

import { calendarApi } from '../../api';
import { CalendarEventCardProps } from '../../types';
import { useDealActivityMutations } from '../../../api';

interface CalendarEventFormValues {
  notes: string;
  account: string;
  subject: string;
  due: string | null;
  eventType: DealActivityEventType;
  activityStatus: DealActivityStatus;
}

export const CalendarEventForm = ({ event, onSwitchMode }: CalendarEventCardProps) => {
  const { handleUpdateDealActivity, isDealActivityUpdating } = useDealActivityMutations();

  const { control, handleSubmit } = useForm<CalendarEventFormValues>({
    defaultValues: {
      account: '',
      subject: event.subject,
      notes: event.notes ?? '',
      eventType: event.eventType,
      activityStatus: event.activityStatus,
      due: event.due?.dueAt ?? event.endAt ?? event.endDate ?? null,
    },
  });

  const onSubmit = async (data: CalendarEventFormValues) => {
    await handleUpdateDealActivity(
      {
        id: event.id,
        type: event.type,
        parentId: event.parentId,
        parentType: DealActivityLinkType.Account,
        event: {
          due: data.due,
          notes: data.notes,
          subject: data.subject,
          accountId: data.account,
          eventType: data.eventType,
          activityStatus: data.activityStatus,
        },
      },
      () => ({
        awaitRefetchQueries: true,
        refetchQueries: [calendarApi.dealActivity],
      })
    );

    onSwitchMode();
  };

  return (
    <>
      <Stack height="100%" direction="column" gap="1rem">
        <InputField
          label="Subject"
          name="subject"
          control={control}
          inputProps={{ fullWidth: true, variant: 'outlined' }}
        />

        <Divider sx={{ borderColor: 'divider' }} />

        <Box sx={{ display: 'grid', gridGap: '1rem', gridTemplateColumns: '1fr 1fr' }}>
          <AutocompleteField
            options={[]}
            name="account"
            control={control}
            textFieldProps={{ label: 'Account', variant: 'outlined', size: 'small' }}
          />

          <SelectField
            label="Type"
            name="eventType"
            control={control}
            variant="outlined"
            options={convertEnumToSelectOptions(DealActivityEventType)}
          />
        </Box>

        <Box sx={{ display: 'grid', gridGap: '1rem', gridTemplateColumns: '1fr 1fr' }}>
          <DateField
            label="Due"
            name="due"
            control={control}
            slotProps={{ textField: { variant: 'outlined', size: 'small' } }}
          />

          <SelectField
            label="Status"
            control={control}
            variant="outlined"
            name="activityStatus"
            options={convertEnumToSelectOptions(DealActivityStatus)}
          />
        </Box>

        <InputField
          label="Notes"
          name="notes"
          control={control}
          inputProps={{
            minRows: 3,
            multiline: true,
            fullWidth: true,
            variant: 'outlined',
            placeholder: 'Add notes...',
          }}
        />
      </Stack>

      <Stack direction="row" gap="1rem" width="100%">
        <Button
          fullWidth
          variant="contained"
          startIcon={<Save />}
          onClick={handleSubmit(onSubmit)}
          loading={isDealActivityUpdating}
        >
          Save
        </Button>

        <Button fullWidth onClick={onSwitchMode} startIcon={<Cancel />} disabled={isDealActivityUpdating}>
          Cancel
        </Button>
      </Stack>
    </>
  );
};
